import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Container, Grid } from '@mui/joy';

import i18n from '@/i18n'

import { useParams } from 'react-router-dom';

import { Img } from '@/components/Img';
import { HBox } from '@/components/HBox';
import { Link } from 'react-router-dom';
import { Link as JoyLink } from '@mui/joy';
import { AppleLogo, SpotifyLogo, TidalLogo } from '@phosphor-icons/react';
import { useResource } from '@/hooks';
import { UserView } from '@/components/UserView';
import { Do, Empty, ForEach } from '@/components/Syntax';
import { ReleaseCard } from '@/components/ReleaseCard';
import { Typography } from '@mui/joy';
import { useManager } from '@/hooks';
import LoadingPage from '@/pages/loading';
import ErrorPage from '@/pages/error';

export function ArtistView({ artistId }) {
  const manager = useManager()
  const { data: artist, isLoading, error, refetch } = useResource({
    queryKey: [manager?.type, manager?.slug, 'artist', artistId],
    path: `/${manager?.type}/${manager?.slug}/artist/${artistId}`,
    enabled: !!manager
  })

  const { data: releasesData } = useResource({
    queryKey: [manager?.type, manager?.slug, 'artist', artistId, 'releases'],
    path: `/${manager?.type}/${manager?.slug}/artist/${artistId}/releases`,
    enabled: !!manager
  })

  if (error) {
    return (
      <ErrorPage onTryAgainClick={() => refetch()} />
    ) 
  }

  if (isLoading) {
    return (
      <LoadingPage />
    )
  }

  if (artist) {
    return (
      <Container>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
          <Typography level="h1">{artist.name}</Typography>
          <Button
            aria-label={i18n.t("edit-artist")}
            component={Link}
            to={`edit`}
            variant="outlined"
          >{i18n.t('edit')}</Button>
          <Img src={artist.image_url} style={{ marginTop: 1, width: '120pt' }} />
          <Box sx={{ gap: 1, display: 'flex', flexDirection: 'row', alignItems: 'center' }} className="links">
            {artist.spotify_id && (
              <JoyLink target="_blank" component={Link} to={`https://open.spotify.com/artist/${artist.spotify_id}`}>
                <SpotifyLogo weight="bold" size={28} />&nbsp;<Typography>Spotify</Typography>
              </JoyLink>
            )}
            {artist.apple_music_id && (
              <JoyLink component={Link} to={`https://music.apple.com/artist/${artist.apple_music_id}`}>
                <AppleLogo />&nbsp;<Typography>Apple Music</Typography>
              </JoyLink>
            )}
            {artist.tidal_id && (
              <JoyLink component={Link} to={`https://tidal.com/browse/artist/${artist.tidal_id}`}>
                <TidalLogo />&nbsp;<Typography>Tidal</Typography>
              </JoyLink>
            )}
          </Box>
          <Typography level="h2">{i18n.t('users')}</Typography>
          <HBox sx={{ gap: 1 }}>
            <ForEach collection={artist.artist_users}>
              <Do>
                {member => (
                  <UserView key={member.id} user={member.user} />
                )}
              </Do>
              <Empty>
                <Typography fontStyle="italic">{i18n.t('artist-has-no-users')}</Typography>
              </Empty>
            </ForEach>
          </HBox>
          <Typography level="h2">{i18n.t('releases')}</Typography>
          <Grid sx={{ alignSelf: 'stretch' }} container spacing={2}>
            <ForEach collection={releasesData?.results}>
              <Do>
                {release => (
                  <Grid item key={release.id} sm={4} md={4} lg={3}> 
                    <ReleaseCard
                      component={Link}
                      to={`../../release/${release.id}`}
                      {...release}
                    />
                  </Grid>
                )}
              </Do>
              <Empty>
                <Typography fontStyle="italic">{i18n.t('artist-has-not-yet-uploaded-any-releases')}</Typography>
              </Empty>
            </ForEach>
          </Grid>
        </Box>
      </Container>
    )
  } else {
    return (
      <LoadingPage />
    )
  }
}

ArtistView.propTypes = {
  artistId: PropTypes.string
}

export function ArtistPage() {
  const { artistId } = useParams()

  return (
    <React.Suspense fallback={<LoadingPage />}>
      <ArtistView artistId={artistId} />
    </React.Suspense>
  )
}

ArtistPage.propTypes = {
  artistId: PropTypes.string
}

export default ArtistPage;
